import { Timestamp } from "firebase/firestore";
import { DanceEvent, EventPrice, ExternalEvent, Location } from "src/types";
import { dateRangeFormat } from "../utils/datetime";
import { eventPriceSort } from "../utils/types";
import { WalletLinks } from "./wallet";
import { CalendarLinks } from "./calendar_links";
import { renderPlaylistEmbeds } from "./playlists";

const renderPass = (
  event: DanceEvent,
  personId?: string,
  isConcession?: boolean
) => {
  const attendance = event.attendees?.find(
    (attendee) => attendee.id === personId
  );
  const prices = event.prices
    ?.filter(
      (price) =>
        (!price.available_until || price.available_until > Timestamp.now()) &&
        !price.publicHidden &&
        price.full_price
    )
    .sort(eventPriceSort);

  const renderPrice = (price: EventPrice) => {
    const multiPrice = price.concession && price.concession != price.full_price;
    const cost =
      isConcession && price.concession ? price.concession : price.full_price;

    const until =
      price.available_until &&
      dateRangeFormat(
        price.available_until,
        undefined,
        undefined,
        event.timezone
      );
    return (
      <div>
        <strong>{price.name}</strong> - ${cost}
        {until && ", available until " + until}
        {multiPrice &&
          ` ($${price.full_price} full, $${price.concession} concession)`}
      </div>
    );
  };

  const paymentDetails = event.payment_details && (
    <div class="pt">
      <strong>Payment details:</strong>
      <div style={{ whiteSpace: "pre-line" }}>{event.payment_details}</div>
    </div>
  );

  if (attendance) {
    return (
      <>
        <div class="pt">
          You are booked in:{" "}
          <strong>{attendance.pass_name || "payment pending"}</strong>
        </div>
        <WalletLinks google_wallet_link={attendance.google_wallet_link} />
      </>
    );
  }
  if (prices?.length) {
    return (
      <div class="pt">
        <strong>Prices:</strong>
        {prices.map(renderPrice)}
        {paymentDetails}
      </div>
    );
  }
  if (paymentDetails) {
    return <div class="pt">{paymentDetails}</div>;
  }
  return;
};

interface RenderEventProps {
  event: DanceEvent | ExternalEvent;
  showPrices?: boolean;
  personId?: string;
  locations?: Record<string, Location>;
  isConcession?: boolean;
}

export const RenderEvent = ({
  event,
  showPrices = false,
  personId,
  locations,
  isConcession,
}: RenderEventProps) => {
  const description = event.description || event.short_description;

  const past = (event.end || event.start).toDate() < new Date();

  const location = (() => {
    if (typeof event.location === "string") {
      return event.location;
    }
    if (typeof event.location === "object") {
      if (locations && locations[event.location.path]) {
        return locations[event.location.path];
      }
      if ("name" in event.location) {
        return event.location as unknown as Location;
      }
    }
    return;
  })();

  const locationName =
    (typeof location === "object" ? location.name : location) || "";
  const notTBD = !["", "TBD", "TBC"].includes(
    locationName.trim().toLocaleUpperCase()
  );

  const where = () => {
    if (!location) {
      return;
    }
    if (typeof location === "string") {
      return <div class="pt">{location}</div>;
    }
    if (past) {
      return (
        <div class="pt">
          <strong>{location.name}</strong>
        </div>
      );
    }
    return (
      <div class="pt">
        <strong>{location.name}</strong>
        {location.description && <div>{location.description}</div>}
        <div>{location.address}</div>
        {location.map_link && (
          <div>
            <a href={location.map_link}>{location.map_link}</a>
          </div>
        )}
      </div>
    );
  };

  const urls = () => {
    if (!event.url) {
      return;
    }
    if (typeof event.url === "string") {
      return (
        <div class="pt">
          <a href={decodeURIComponent(event.url)}>
            {decodeURIComponent(event.url)}
          </a>
        </div>
      );
    }
    return (
      <div class="pt">
        {event.url.map((urlLine) => (
          <div>
            <a href={decodeURIComponent(urlLine.url)}>
              {urlLine.name || decodeURIComponent(urlLine.url)}
            </a>
          </div>
        ))}
      </div>
    );
  };

  const descriptionDisplay = () => {
    if (!description) {
      return;
    }
    const lines = description.split(/\n/);
    if (lines.length <= 1) {
      return <div class="pt">{lines}</div>;
    }
    const inner = () => lines.map((line) => <div>{line}</div>);

    if (past) {
      return (
        <details class="pt">
          <summary>Description</summary>
          {inner()}
        </details>
      );
    }
    return <div class="pt">{inner()}</div>;
  };

  return (
    <div key={"event-" + event.id} class="light-border">
      <div>
        <strong>{event.name}</strong>
      </div>
      {notTBD && (
        <div class="pt">
          {dateRangeFormat(event.start, event.end, undefined, event.timezone)}
        </div>
      )}
      {where()}
      {descriptionDisplay()}
      {urls()}
      {showPrices &&
        notTBD &&
        !past &&
        "prices" in event &&
        renderPass(event, personId, isConcession)}
      {"bookable" in event &&
        typeof location === "object" &&
        notTBD &&
        !past && <CalendarLinks event={event} location={location} />}
      {past &&
        "playlist" in event &&
        typeof event.playlist === "object" &&
        renderPlaylistEmbeds(event.playlist, true)}
    </div>
  );
};
